define("tmds/utils/svgUtils", [
	"jquery",
	"d3",
	"tmds/geometry/Extent",
	"tmds/symbols/SimplePointSymbol",
	"tmds/symbols/utils"
], function(
	$, d3, Extent, SimplePointSymbol, symUtils
) {
	"use strict";
	
	/**
	 * check if the input is a valid color value range which is [0..255]
	 * @return {Boolean} true if a valid value; false otherwise.
	 */
	function inValidColorRange(v) {
		return typeof v === "number" && v >= 0 && v <= 255;
	}
	
	/**
	 * check if the input is a valid color array, which is [r,g,b,a], where
	 * 	r, g, b: [0..255]
	 * 	a: [0..1]
	 * @return {Boolean} true if a valid color array; false otherwise.
	 */
	function validateColorArray(rgba) {
		return rgba && 
			Array.isArray(rgba) && 
			inValidColorRange(rgba[0]) &&
			inValidColorRange(rgba[1]) &&
			inValidColorRange(rgba[2]);
	}
	
	/**
	 * convert [r,g,b,a] to D3 color
	 * @param rgb {Array} [r,g,b,a]
	 * @returns {d3.Color}
	 * @note if input is an invalid color array, return black
	 */
	function rgbToColor(rgba) {		
		return validateColorArray(rgba) ? d3.rgb(rgba[0], rgba[1], rgba[2]) : d3.rgb(0,0,0);
	}
	
	/**
	 * extract the opacity value from a color array which is [r,g,b,a]
	 * @return {Number} [0..1]
	 * @note return 1 if input is invalid color array or "a" value is not provided.
	 */
	function toOpacity(rgba) {
		return (validateColorArray(rgba) && rgba[3]) || 1;
	}
	
	/**
	 * get the svg attributes for a point that can be used to create a svg element
	 * @param svgElem {tmds.SvgElemet}
	 * @returns {Object} Object to contain svg element type and its attributes. 
	 */
	function getPointSvgProps(svgElem) {
			var svgProps = {
					type: "path",
					attrs: {}
				};
			
			var sym = svgElem.symbol,
				geom = svgElem.geometry;

			switch (sym.style) {
				case 'circle':
				case 'cross':
				case 'diamond':
				case 'square':
				case 'triangle-down':
				case 'triangle-up':
					svgProps.attrs.d = d3.svg.symbol()
											.type(sym.style)
											.size(sym.size * sym.size)();	// symbol size is in square pixels
					
					break;
				case 'path':
					svgProps.attrs.d = sym.path;
					
					break;
			}

			// apply the symbol offset to the geometry
			var x = geom.x + sym.xOffset,
				y = geom.y + sym.yOffset;
			svgProps.attrs.transform = "translate(" + x + "," + y + ") scale(1)";

			svgProps.attrs.fill = rgbToColor(sym.color);
			svgProps.attrs["fill-opacity"] = toOpacity(sym.color);
			
			// rotate the symbol by symble.angle
			if (sym.angle) {
				svgProps.attrs.transform += " rotate(" + sym.angle + ")";
			}
			
			if (sym.outline) {
				svgProps.attrs = $.extend(svgProps.attrs, {
					stroke: rgbToColor(sym.outline.color),
					"stroke-opacity": toOpacity(sym.outline.color),
					"stroke-width": sym.outline.width
				});
			}
			
			return svgProps;
	}
	
	/**
	 * get the svg attributes for a line that can be used to create a svg element
	 * @param svgElem {tmds.SvgElemet}
	 * @returns {Array} array of svg attributes.
	 */
	function getLineSvgProps(svgElem) {
			var polylinePaths = [];
			var lineCoords = [];
			
			// Accessor function that will take in the data array that is passed to D3.js and 
			// extract the the set x,y coordinates.
			var lineFunction = d3.svg.line()
								.x(function(d) { return d.x; })
								.y(function(d) { return d.y; })
								.interpolate("linear");
			
			var sym = svgElem.symbol,
				geom = svgElem.geometry;
			
			switch(geom.type) {
				case "Line":
					lineCoords.push({x: geom.x1, y: geom.y1});
					lineCoords.push({x: geom.x2, y: geom.y2});
					
					polylinePaths.push(lineCoords);
					
					break;
				case "Polyline":
					geom.paths.forEach(function(path) {
						lineCoords = [];
						path.forEach(function(coord) {
							lineCoords.push({x: coord[0], y: coord[1]});
						});
						
						polylinePaths.push(lineCoords);
					});
					
					break;
			}
			
			var arrSvgProps = [];
			polylinePaths.forEach(function(path) {
				
				if(path[0][0] != undefined) {
					path.forEach(function(_path) {
						arrSvgProps.push({
							type: "path",
							attrs: {
								d: lineFunction(_path),
								stroke: rgbToColor(sym.color),
								"stroke-opacity": toOpacity(sym.color),
								"stroke-width": sym.width,
								"stroke-dasharray": symUtils.toDashArray(sym.style)
							}
						});
					});
				}
				else {
					arrSvgProps.push({
						type: "path",
						attrs: {
							d: lineFunction(path),
							stroke: rgbToColor(sym.color),
							"stroke-opacity": toOpacity(sym.color),
							"stroke-width": sym.width,
							"stroke-dasharray": symUtils.toDashArray(sym.style)
						}
					});
				}
			});
			
			return arrSvgProps;
	}
	
	/**
	 * get the svg attributes for a polygon that can be used to create a svg element
	 * @param svgElem {tmds.SvgElemet}
	 * @returns {Object} Object to contain svg element type and its attributes.
	 */
	function getFillSvgProps(svgElem) {
		var svgProps = {
				type: "path",	// "path" | "rect"
				attrs: {}
			};
		
		var sym = svgElem.symbol,
			geom = svgElem.geometry;

		switch (geom.type) {
			case "Extent":
				svgProps.type = "rect";
				svgProps.attrs = {
					x: geom.xmin,
					y: geom.ymin,
					width: geom.xmax - geom.xmin,
					height: geom.ymax - geom.ymin,
					fill: sym.style === "none" ? "none" : rgbToColor(sym.color),
					"fill-opacity": toOpacity(sym.color),
					stroke: rgbToColor(sym.outline.color),
					"stroke-width": sym.outline.width,
					"stroke-opacity": toOpacity(sym.outline.color),
					"stroke-dasharray": symUtils.toDashArray(sym.outline.style)
				};
				
				break;
		}
		
		return svgProps;
	}
	
	/**
	 * get the svg attributes for a text that can be used to create a text svg element
	 * @param svgElem {tmds.SvgElemet}
	 * @returns {Object} Object to contain svg element type and its attributes.
	 */
	function getTextSvgProps(svgElem) {
		var sym = svgElem.symbol,
			geom = svgElem.geometry;

		return {
			type: "text",
			text: sym.text,
			attrs: {
				fill: rgbToColor(sym.color),
				opacity: toOpacity(sym.color),
				"text-anchor": sym.anchor,
				x: geom.x + sym.xOffset,
				y: geom.y + sym.yOffset,
				"font-family": sym.font.family,
				"font-size": sym.font.size,
				"font-style": sym.font.style,
				"font-weight": sym.font.weight
			}
		};
	}
	
	/**
	 * get the svg attributes for a field text that can be used to create a text svg element based on a field value
	 * @param svgElem {tmds.SvgElemet}
	 * @returns {Object} Object to contain svg element type and its attributes.
	 */
	function getFieldSvgProps(svgElem) {
		var sym = svgElem.symbol,
			geom = svgElem.geometry;

		return {
			type: "text",
			text: svgElem.attributes[sym.field],
			attrs: {
				fill: rgbToColor(sym.color),
				opacity: toOpacity(sym.color),
				"text-anchor": sym.anchor,
				x: geom.x + sym.xOffset,
				y: geom.y + sym.yOffset,
				"font-family": sym.font.family,
				"font-size": sym.font.size,
				"font-style": sym.font.style,
				"font-weight": sym.font.weight
			}
		};
	}

	
	/**
	 * get the svg attributes for a composite point that can be used to create a composite svg element
	 * @param svgElem {tmds.SvgElemet}
	 * @returns {Array} array of svg attributes.
	 */
	function getCompositePointSvgProps(svgElem) {
		var arrSvgProps = [],
			svgProps;
		
		var sym = svgElem.symbol,
			geom = svgElem.geometry,
			attrs = svgElem.attributes;
		
		$.each(sym.symbols, function(_, symbol) {
			var elem = {geometry: geom, symbol: symbol, attributes: attrs};
			
			switch(symbol.type) {
				case "SimplePointSymbol":
					svgProps = getPointSvgProps(elem);
					break;
				case "TextSymbol":
					svgProps = getTextSvgProps(elem);
					break;
				case "FieldSymbol":
					svgProps = getFieldSvgProps(elem);
					break;
			}
			
			arrSvgProps.push(svgProps);
		});
		
		return arrSvgProps;
	}
	
	
	/**
	 * get the svg element type
	 * @param svgElem {tmds.SvgElement}
	 * @return {Array} array of svg option objects with the following structure
	 * 	[{
	 * 		svgType: circle | cross | diamond | square | triangle-down | triangle-up | line | polyline | polygon | text | path,
	 * 		svgAttrs: {}
	 * 	},...] 
	 */
	function getSvgAttrs(svgElem) {
		var arrSvgProps = [];
		
		var sym = svgElem.symbol,
			geom = svgElem.geometry;
		
		// TODO: implement to return the svg type and its attributes
		switch (sym.type) {
			case "SimpleFillSymbol":
				arrSvgProps.push(getFillSvgProps(svgElem));
				
				break;
			case "SimplePointSymbol":
				arrSvgProps.push(getPointSvgProps(svgElem));
				
				break;
			case "SimpleLineSymbol":
				arrSvgProps = getLineSvgProps(svgElem);
				
				break;
			case "TextSymbol":
				arrSvgProps.push(getTextSvgProps(svgElem));
				
				break;
			case "CompositePointSymbol": 
				arrSvgProps = getCompositePointSvgProps(svgElem);
				
				break;
			case "FieldSymbol":
				arrSvgProps.push(getFieldSvgProps(svgElem));
				
				break;
			default:
				break;
		}
		
		return arrSvgProps;
	}
	
	/**
	 * return extent that wraps a set of svg elements
	 * @return {tmds.geometry.Extent}
	 */
	function svgElementsExtent(elems) {
		var exts = elems.map(function(elem) {
			var geom = elem.geometry;
			
			if (geom.type === "Point") {
				return new Extent({
					xmin: geom.x,
					xmax: geom.x,
					ymin: geom.y,
					ymax: geom.y
				});
			}
			else {
				return geom.toExtent();	
			}
		});
		
		var extent;
		exts.forEach(function(anExt, idx) {
			extent = idx === 0 ? anExt : extent.union(anExt);
		});
		
		return extent;
	}
	
	var svgUtils = {
		'getSvgAttrs' : getSvgAttrs,
		'svgElementsExtent' : svgElementsExtent
	};
	
	return svgUtils;
});
